import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, mergeProps as _mergeProps, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-7c213738"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "note-modal-content"
}
const _hoisted_2 = { class: "small-field-container" }
const _hoisted_3 = { class: "field-column" }
const _hoisted_4 = { class: "field-column" }

import { inject, ref, computed, watch } from "vue";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { getErrorMessage } from "bright-logic-core/src/errors";
import displayErrorMessage from "@/utils/ErrorHandling";
import modalBase from "@/components/ui/modals/modal-base.vue";
import lookupSingleSelect from "@/components/ui/lookup/lookup-single-select.vue";
import { LookupTableSource } from "acquaint-api/constants/LookupTableSource";
import propertyLookup from "@/components/ui/lookup/property-lookup.vue";
import contactLookup from "@/components/ui/lookup/contact-lookup.vue";
import { ContactTasks } from "acquaint-api/models/ContactTasks";
import { DxToolbarItem } from "devextreme-vue/popup";
import { IDDescriptionPair } from "@/types/generic/IDDescriptionPair";
import switchToggle from "@/components/ui/toggles/switch-toggle.vue";
import AcquaintTextbox from "@/components/ui/inputs/acquaint-textbox.vue";
import AcquaintTextArea from "@/components/ui/inputs/acquaint-text-area.vue";
import { ContactTasksValidation } from "acquaint-api/validation/ContactTasksValidation";
import { ModalWidth } from "@/types/ui/modals/ModalWidth";
import FieldValidationFreeDateBox from "@/components/ui/inputs/field-validation-free-date-box.vue";
import { TimeStamps } from "@/utils/TimeStamp";
import { TasksNotes } from "@/enums/TasksNotes";
import { ValidationCallbackParams } from "@/types/validation/ValidationCallbackParams";
import { useSystemConfigurationStore } from "@/stores/SystemConfigurationStore";
import { SystemConfiguration } from "acquaint-api/constants/SystemConfiguration";
import { TaskTemplates } from "acquaint-api/models/TaskTemplates";
import TaskTemplatesConfigurationModal from "@/components/contact-configuration/task-templates-configuration-modal.vue";
import { DxContextMenu } from "devextreme-vue/context-menu";
import { ContextMenuItem } from "@/types/ui/menus/ContextMenuItem";
import { TaskType } from "@/enums/tasks/TaskType";

const systemUserId = 999999999;

export default /*#__PURE__*/_defineComponent({
  __name: 'edit-note-modal',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },
  /**
   * The unique identifier for the notes.
   * @type {number}
   * @required
   */
  id: {
    type: Number,
    required: true,
  },
  /**
   * The unique identifier for the contact.
   * @type {number}
   * @default 0
   */
  contactId: {
    type: Number,
    default: 0,
  },
  /**
   * The unique identifier for the property.
   * @type {number}
   * @required
   */
  propertyID: {
    type: Number,
    default: 0,
  },
  /**
   * The unique identifier for the work order record.
   * @type {number}
   * @required
   */
  workOrderID: {
    type: Number,
    default: 0,
  },
  /**
   * The unique identifier for the certificate.
   * @type {number}
   * @required
   */
  certificateID: {
    type: Number,
    default: 0,
  },
  /**
   * The unique identifier for the task type.
   * @type {number}
   * @default 0
   */
  taskTypeId: {
    type: Number,
    default: 0,
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
   * @property {boolean} value - Indicates whether the modal has been opened or closed.
   */
  "update:modelValue",
  /**
   * Triggered when the note is saved. This event passes whether the note is related to the provided contact and property.
   * @property {boolean} contactMatch - Indicates whether the note is related to the provided contact ID.
   * @property {boolean} propertyMatch - Indicates whether the note is related to the provided property ID.
   */
  "saveNote",
],
  setup(__props, { emit }) {

const props = __props;





const systemConfigStore = useSystemConfigurationStore();
const acquaintApiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const noteDetails = ref<ContactTasks>();
const initalNoteDetails = ref<ContactTasks>();
const ownerName = ref<IDDescriptionPair>();
const saving = ref(false);
const showCloseConfirmation = ref(false);
const keepingModalOpening = ref(false);
const focusBodyField = ref(false);
const resetValid = ref(false);
const contactRequired = ref(false);
const openTemplateModal = ref(false);
const taskTemplate = ref<TaskTemplates>({
  id: 0,
  description: "",
  subject: "",
  body: "",
  taskTypeID: 0,
  inActive: 0,
});
const showFunctionsMenu = ref(false);
const contextMenuId = ref("note-modal-functions-list");

const showNoteModal = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

watch(
  () => showNoteModal.value,
  async () => {
    // Avoid unnecessary api calls by only calling api when modal is shown
    if (!showNoteModal.value || keepingModalOpening.value) {
      return;
    }

    contextMenuId.value = `note-modal-functions-list-${Date.now()}`;
    contactRequired.value = false;
    await populateData();
    resetValid.value = true;

    setTimeout(() => {
      resetValid.value = false;
    }, 100);
    await fetchOwnerName();
  },
);

// Functions for fetching the data
const fetchData = async () => {
  try {
    if (props.id != 0) {
      initalNoteDetails.value = await acquaintApiClient.httpGet(
        `database/contacttasks/${props.id}`,
      );
    } else {
      initalNoteDetails.value = await acquaintApiClient.httpGet(
        "database/contacttasks/defaultmodel",
      );
    }
    return true;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

const populateData = async () => {
  if (!(await fetchData())) {
    initalNoteDetails.value = undefined;
    noteDetails.value = undefined;
    return;
  }

  // Set this new Task record to be a note record.
  if (initalNoteDetails.value) {
    /* Get default note type from system config and assign it to the model
     * Note: the system config store always returns a string.
     * To maintain type safety this needs to be converted to a number */
    if (initalNoteDetails.value.taskTypeID < 1) {
      const defaultTaskTypeId =
        await systemConfigStore.getSystemConfigurationValue(
          SystemConfiguration.DefaultNoteType,
        );
      initalNoteDetails.value.taskTypeID = Number(defaultTaskTypeId);
    }

    initalNoteDetails.value.type = TasksNotes.Note;

    if (props.taskTypeId !== 0) {
      initalNoteDetails.value.taskTypeID = props.taskTypeId;
    }

    if (props.contactId > -1) {
      initalNoteDetails.value.contactID = props.contactId;
    }

    if (props.propertyID > 0) {
      initalNoteDetails.value.propertyID = props.propertyID;
    }

    if (props.workOrderID > 0) {
      initalNoteDetails.value.workOrderID = props.workOrderID;
    }

    if (props.certificateID > 0) {
      initalNoteDetails.value.certificateID = props.certificateID;
    }

    noteDetails.value = JSON.parse(JSON.stringify(initalNoteDetails.value));
  }
};

const fetchOwnerName = async () => {
  // Ignore system user as this will cause api to error
  if (noteDetails.value?.userID === systemUserId) {
    ownerName.value = { id: systemUserId, description: "System User" };
    return;
  }

  try {
    ownerName.value = await acquaintApiClient.httpGet(
      `app/contacts/${noteDetails.value?.userID}/name`,
    );
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
  }
};

// After a template has been selected, we must replace the note's subject and body with the template's subject and body
const fetchItemData = async (templateId: number) => {
  try {
    taskTemplate.value = await acquaintApiClient.httpGet<TaskTemplates>(
      `database/tasktemplates/${templateId}`,
    );

    return true;
  } catch (err) {
    taskTemplate.value = {
      id: 0,
      description: "",
      subject: "",
      body: "",
      taskTypeID: 0,
      inActive: 0,
    };
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

const populateSubjectBodyFields = async (templateId: number) => {
  if ((await fetchItemData(templateId)) && noteDetails.value) {
    noteDetails.value.subject = taskTemplate.value.subject;
    noteDetails.value.body = taskTemplate.value.body;
  }
};

//Functions for saving the record
/** Pushes the updated task to the api */
const submitData = async () => {
  try {
    if (props.id > 0) {
      await acquaintApiClient.httpPut(
        `database/contacttasks/${props.id}`,
        noteDetails.value,
      );
    } else {
      await acquaintApiClient.httpPost(
        `database/contacttasks`,
        noteDetails.value,
      );
    }
    return true;
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

/** Handles the Save button click event */
const onSaveClick = async (e: any) => {
  contactRequired.value = true;
  const validate = e.validationGroup.validate();

  if (!e.validationGroup._validationInfo.result.isValid || !validate.isValid) {
    return;
  }

  if (!noteDetails.value?.contactID && !noteDetails.value?.propertyID) {
    displayErrorMessage(
      "You must link the Note to either a Contact or Property.",
    );
    return;
  }

  if (!(await submitData())) {
    return;
  }

  showCloseConfirmation.value = false;
  closeTaskModal();
  emit(
    "saveNote",
    noteDetails.value?.contactID == props.contactId,
    noteDetails.value?.propertyID == props.propertyID,
  );
};

// Code relating to the closure of the modal
/** Closes the task modal */
const closeTaskModal = () => {
  showNoteModal.value = false;
};

watch(
  () => noteDetails.value,
  (value) => {
    showCloseConfirmation.value =
      JSON.stringify(value) != JSON.stringify(initalNoteDetails.value);
  },
  { deep: true },
);

/** Adds a date stamp to the end of the current taskbody */
const dateStamp = async () => {
  if (!noteDetails.value) {
    return;
  }

  noteDetails.value.body = await TimeStamps(noteDetails.value.body);
  focusBodyField.value = true;

  setTimeout(() => {
    focusBodyField.value = false;
  }, 50);
};

// Code for validating the data before it is submitted
// This function is passed down to either the body or subject field and is used in their respective custom rule tag.
// Either has to have text in it. True is returned when text has been entered (the value is valid) otherwise, false is returned and a user error message appears.

const customValidationFunction = (params: ValidationCallbackParams) => {
  return typeof params.value === "string" ? params.value.length > 0 : false;
};

const requiredMessage = ref("The subject or the body field is required");

//If the subject field does have text in it, the customValidationFunction will not passed down to the body field, requiring the user to enter a value, as only one of the two fields needs to have text in it
const conditionalBodyValidation = computed(() => {
  return noteDetails.value?.subject
    ? undefined
    : {
        customValidation: customValidationFunction,
      };
});

//If the body field does have text in it, the customValidationFunction will not be passed down to the subject field, requiring the user to enter a value, as only one of the two fields needs to have text in it
const conditionalSubjectValidation = computed(() => {
  return noteDetails.value?.body
    ? undefined
    : {
        customValidation: customValidationFunction,
      };
});

// This code handles the actions footer menu.
const menuItems: ContextMenuItem[] = [
  {
    text: "Template",
    key: "template",
    icon: "",
  },
  {
    text: "Date Stamp",
    key: "dateStamp",
    icon: "",
  },
];

const onMenuItemClick = (e: any) => {
  switch (e.itemData.key) {
    case "template":
      openTemplateModal.value = true;
      break;
    case "dateStamp":
      dateStamp();
      break;
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(modalBase, {
      modelValue: showNoteModal.value,
      "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((showNoteModal).value = $event)),
      title: "Note",
      width: _unref(ModalWidth).Small,
      height: "650px",
      saving: saving.value,
      showCloseConfirmationPrompt: showCloseConfirmation.value,
      onModalClosed: _cache[11] || (_cache[11] = ($event: any) => (keepingModalOpening.value = false)),
      onCancelPromptDenied: _cache[12] || (_cache[12] = ($event: any) => (keepingModalOpening.value = true)),
      "help-page": "notes"
    }, {
      content: _withCtx(() => [
        (noteDetails.value)
          ? (_openBlock(), _createElementBlock("section", _hoisted_1, [
              _createVNode(contactLookup, {
                label: "Contact",
                findNameOnIdChange: true,
                modelValue: noteDetails.value.contactID,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((noteDetails.value.contactID) = $event)),
                readOnly: false,
                isRequired: contactRequired.value,
                showNavigateContactIcon: false
              }, null, 8, ["modelValue", "isRequired"]),
              _createVNode(propertyLookup, {
                label: "Property",
                modelValue: noteDetails.value.propertyID,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((noteDetails.value.propertyID) = $event)),
                displayNavigateIcon: false,
                displayPeek: true,
                readOnly: _unref(TaskType).Progression === __props.taskTypeId && __props.propertyID > 0
              }, null, 8, ["modelValue", "readOnly"]),
              _createVNode(AcquaintTextbox, _mergeProps({
                label: "Subject",
                fieldValidation: _unref(ContactTasksValidation).Subject,
                modelValue: noteDetails.value.subject,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((noteDetails.value.subject) = $event)),
                invalidMessage: requiredMessage.value
              }, conditionalSubjectValidation.value, {
                resetValid: resetValid.value,
                "validation-group": "edit-note-modal"
              }), null, 16, ["fieldValidation", "modelValue", "invalidMessage", "resetValid"]),
              _createVNode(AcquaintTextArea, _mergeProps({
                label: "Body",
                fieldValidation: _unref(ContactTasksValidation).Body,
                modelValue: noteDetails.value.body,
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((noteDetails.value.body) = $event)),
                invalidMessage: requiredMessage.value
              }, conditionalBodyValidation.value, {
                focusInput: focusBodyField.value,
                resetValid: resetValid.value,
                "validation-group": "edit-note-modal"
              }), null, 16, ["fieldValidation", "modelValue", "invalidMessage", "focusInput", "resetValid"]),
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  _createVNode(FieldValidationFreeDateBox, {
                    includeTime: true,
                    label: "Date Added",
                    modelValue: noteDetails.value.addedDate,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((noteDetails.value.addedDate) = $event)),
                    readOnly: true
                  }, null, 8, ["modelValue"]),
                  _createVNode(FieldValidationFreeDateBox, {
                    includeTime: true,
                    label: "Date Updated",
                    modelValue: noteDetails.value.updatedDate,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((noteDetails.value.updatedDate) = $event)),
                    readOnly: true
                  }, null, 8, ["modelValue"])
                ]),
                _createElementVNode("div", _hoisted_4, [
                  _createVNode(lookupSingleSelect, {
                    modelValue: noteDetails.value.taskTypeID,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((noteDetails.value.taskTypeID) = $event)),
                    "lookup-source": _unref(LookupTableSource).TaskTypes,
                    label: "Type",
                    requiredInput: true,
                    "validation-group": "edit-note-modal",
                    readOnly: _unref(TaskType).Progression === __props.taskTypeId
                  }, null, 8, ["modelValue", "lookup-source", "readOnly"]),
                  _createVNode(lookupSingleSelect, {
                    modelValue: noteDetails.value.userID,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((noteDetails.value.userID) = $event)),
                    "lookup-source": _unref(LookupTableSource).Users,
                    label: "Owner",
                    readOnly: true,
                    updateTableSourceOnIdChange: true,
                    "validation-group": "edit-note-modal"
                  }, null, 8, ["modelValue", "lookup-source"])
                ])
              ]),
              _createElementVNode("div", null, [
                _createVNode(switchToggle, {
                  label: "Always Show At Top",
                  modelValue: noteDetails.value.alwaysShowAtTop,
                  "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((noteDetails.value.alwaysShowAtTop) = $event)),
                  "label-width": "200px",
                  readOnly: false
                }, null, 8, ["modelValue"]),
                _createVNode(switchToggle, {
                  label: "Desktop Alert",
                  modelValue: noteDetails.value.desktopAlert,
                  "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((noteDetails.value.desktopAlert) = $event)),
                  "label-width": "200px",
                  readOnly: false
                }, null, 8, ["modelValue"])
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          elementAttr: { id: contextMenuId.value },
          text: 'Actions',
          onClick: () => {
            showFunctionsMenu.value = !showFunctionsMenu.value;
          },
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Save',
          onClick: onSaveClick,
          validationGroup: 'edit-note-modal',
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Cancel',
          onClick: () => {
            closeTaskModal();
          },
        }
        }, null, 8, ["options"])
      ]),
      _: 1
    }, 8, ["modelValue", "width", "saving", "showCloseConfirmationPrompt"]),
    _createVNode(TaskTemplatesConfigurationModal, {
      modelValue: openTemplateModal.value,
      "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((openTemplateModal).value = $event)),
      onEmitSelectedDetails: populateSubjectBodyFields,
      showSelectButton: true
    }, null, 8, ["modelValue"]),
    _createVNode(_unref(DxContextMenu), {
      visible: showFunctionsMenu.value,
      "onUpdate:visible": _cache[14] || (_cache[14] = ($event: any) => ((showFunctionsMenu).value = $event)),
      "data-source": menuItems,
      target: `#${contextMenuId.value}`,
      position: { my: 'bottom right', at: 'top right' },
      onItemClick: onMenuItemClick
    }, null, 8, ["visible", "target"])
  ], 64))
}
}

})